import React, { FC, ReactNode, useRef, useState } from 'react';
import classNames from 'classnames';
import { Tooltip } from '../components/Tooltip';
import { useDelayedHover } from './AvailablePricesSelector/useDelayedHover';
import { LoadIndicator } from './LoadIndicator';
import { LoanFormatIcon } from './GildIcon';
import { LoanFormat } from '../../../models/LoanFormat';
import { useTranslation } from 'react-i18next';
import FormatCurrency from '../utils/formatCurrency';

type BadgeProps = {
    readonly icon?: string;
    readonly tooltip?: ReactNode;
    readonly isLoading?: boolean;
    readonly children: ReactNode;
};

const Badge: FC<BadgeProps> = ({ icon, tooltip, isLoading, children }) => {
    const [tooltipVisible, setTooltipVisible] = useState(false);
    const badgeRef = useRef<HTMLSpanElement>(null);
    const { onMouseEnter, onMouseLeave } = useDelayedHover({
        onHover: setTooltipVisible,
        delayActive: false
    });

    return (
        <>
            <span
                ref={badgeRef}
                className="marker-badge margin display-inline-flex"
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            >
                {icon ? <LoanFormatIcon icon={icon} className="loanformat-icon-small" /> : null}
                <span>{isLoading ? <LoadIndicator /> : children}</span>
            </span>
            {tooltip && (
                <Tooltip target={badgeRef} visible={tooltipVisible} placement="top">
                    {tooltip}
                </Tooltip>
            )}
        </>
    );
};

type TotalAmountBadgeProps = {
    readonly loanFormat: LoanFormat;
    readonly amount: number;
    readonly value?: number;
    readonly currency?: string;
    readonly isLoading?: boolean;
};

function getLoanFormatIcon(loanFormat: LoanFormat) {
    switch (loanFormat) {
        case LoanFormat.eBooks:
            return 'ebook-filled';
        case LoanFormat.eAudiobooks:
            return 'eaudiobook-filled';
        case LoanFormat.ePress:
        case LoanFormat.ePressIssues:
        case LoanFormat.eMagazines:
        case LoanFormat.eMagazineIssues:
            return 'emagazine-filled';
    }
}

const TotalAmountBadge: FC<TotalAmountBadgeProps> = ({ loanFormat, amount, value, currency, isLoading }) => {
    const { t } = useTranslation();
    if (amount <= 0) {
        return null;
    }
    const badgeProps = {
        icon: getLoanFormatIcon(loanFormat),
        tooltip: t('loanFormat.copiesTextWithCount', {
            context: loanFormat.toLowerCase(),
            count: amount
        })
    };
    const total = t('numberFormat.numberWithCurrencySymbol', {
        val: FormatCurrency.convertIntegerToCurrencyFormat(value),
        currencySymbol: FormatCurrency.mapCurrencyToSymbol(currency)
    });

    return (
        <>
            <Badge {...badgeProps}>{amount}</Badge>
            <Badge>{isLoading ? <LoadIndicator /> : total}</Badge>
        </>
    );
};

type TotalAmountBadgesProps = {
    readonly currency?: string;
    readonly ebooksAmount?: number;
    readonly ebooksValue?: number;
    readonly eaudiobooksAmount?: number;
    readonly eaudiobooksValue?: number;
    readonly epressAmount?: number;
    readonly epressValue?: number;
    readonly className?: string;
    readonly isLoading?: boolean;
};

const TotalAmountBadges: FC<TotalAmountBadgesProps> = ({
    ebooksAmount = 0,
    ebooksValue,
    eaudiobooksAmount = 0,
    eaudiobooksValue,
    epressAmount = 0,
    epressValue,
    currency,
    className,
    isLoading
}) => {
    return (
        <div className={classNames('total-amount-badges display-inline-flex gild', className)}>
            <TotalAmountBadge
                loanFormat={LoanFormat.eBooks}
                amount={ebooksAmount}
                value={ebooksValue}
                currency={currency}
                isLoading={isLoading}
            />
            <TotalAmountBadge
                loanFormat={LoanFormat.eAudiobooks}
                amount={eaudiobooksAmount}
                value={eaudiobooksValue}
                currency={currency}
                isLoading={isLoading}
            />
            <TotalAmountBadge
                loanFormat={LoanFormat.ePress}
                amount={epressAmount}
                value={epressValue}
                currency={currency}
                isLoading={isLoading}
            />
        </div>
    );
};

export default TotalAmountBadges;
